"use client";

import React from "react";
import { motion } from "framer-motion";
import { Search, TrendingUp, MapPin, Target, Users, CircleCheck as CheckCircle2, ArrowRight, ChartBar as BarChart2, Building2, Zap, Globe as Globe2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const insights = [{
  icon: Search,
  title: "Commercial Local Search Intent",
  description: "Doncaster buyers display high-intent search behavior. Searching for commercial services and high-value B2B solutions in South Yorkshire starts with local Google queries that convert directly into inquiries."
}, {
  icon: Target,
  title: "The Regional Competitor Gap",
  description: "Many Doncaster businesses rely on generic agency retainers or outdated websites. Direct technical SEO and precise content structuring allow local brands to swiftly claim top spots over passive competitors."
}, {
  icon: MapPin,
  title: "Strategic Cross-Border Visibility",
  description: "Doncaster's prime transport links make it a hub for logistics and trade. My SEO strategy captures hyper-local map queries while expanding organic reach across Sheffield, Rotherham, Leeds, and Wakefield."
}, {
  icon: Zap,
  title: "AI Search & AI Overviews Readiness",
  description: "Google AI Overviews and AI search engines actively answer local queries. I optimize your digital presence so AI search platforms cite your brand as the primary authority in Doncaster."
}];
const sectorMetrics = [{
  sector: "B2B & Logistics Hubs",
  growth: "+42%",
  label: "YoY Search Volume Growth",
  detail: "High demand for regional supply chain, warehouse, and commercial service keywords."
}, {
  sector: "Commercial & Field Services",
  growth: "+58%",
  label: "Mobile Local 'Near Me' Queries",
  detail: "Doncaster residents and site managers actively search for rapid local service providers."
}, {
  sector: "Professional & Legal Services",
  growth: "68%",
  label: "Search-Initiated Inquiries",
  detail: "Corporate and individual clients research credentials thoroughly before booking consultations."
}, {
  sector: "E-commerce & Distribution",
  growth: "+35%",
  label: "National Organic Reach",
  detail: "Doncaster-based product manufacturers reaching UK-wide buyers through structured technical SEO."
}];
const methodologySteps = ["In-depth Doncaster keyword research focused on commercial intent, not vanity traffic.", "Google Business Profile & local Map Pack alignment for maximum South Yorkshire visibility.", "Technical audit and core web vitals optimization to beat regional competitors.", "Transparent monthly reporting directly from me — no account managers or generic PDFs."];
export default function Doncaster() {
  return <section id="doncastermarketanalysis" data-nav="light" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Glows */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-secondary/40 rounded-full blur-3xl -z-10 pointer-events-none" />
      <div className="absolute bottom-10 left-10 w-80 h-80 bg-accent/10 rounded-full blur-3xl -z-10 pointer-events-none" />

      <div className="max-w-7xl mx-auto space-y-16">
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }}>
            <Badge variant="secondary" className="px-3 py-1 text-xs font-medium border border-border">
              <MapPin className="w-3.5 h-3.5 text-primary mr-1" />
              Doncaster Local SEO Landscape
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-primary font-serif">
            Doncaster Search Landscape & Market Growth Opportunity
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            A clear, data-driven analysis of how local customers and regional buyers search in Doncaster. Discover how direct, expert-led SEO captures high-value search intent across South Yorkshire.
          </motion.p>
        </div>

        {/* 4 Pillar Strategic Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {insights.map((insight, i) => {
          const Icon = insight.icon;
          return <motion.div key={insight.title} data-index={i} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }}>
                <Card className="h-full border border-border bg-card shadow-sm hover:border-primary/30 transition-[border-color,box-shadow] duration-300">
                  <CardHeader className="flex flex-row items-center gap-4 pb-2">
                    <div className="p-2.5 rounded-lg bg-secondary text-primary shrink-0">
                      <Icon className="w-6 h-6" />
                    </div>
                    <CardTitle className="text-xl font-semibold text-primary font-serif">
                      {insight.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <CardDescription className="text-foreground/80 text-sm leading-relaxed font-default">
                      {insight.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Doncaster Market Metrics & Search Data */}
        <div className="bg-muted/40 border border-border rounded-2xl p-6 sm:p-10 space-y-8 shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <div className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                Market Dynamics
              </span>
              <h3 className="text-2xl sm:text-3xl font-semibold text-primary font-serif">
                Key Doncaster Sectors & Search Growth
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md font-default">
              Search trends indicate growing online commercial intent across Doncaster's dominant business sectors.
            </p>
          </div>

          <Separator className="bg-border" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {sectorMetrics.map((metric, i) => <div key={metric.sector} data-index={i} className="bg-card border border-border rounded-xl p-5 flex flex-col justify-between space-y-4 shadow-xs">
                <div className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground uppercase">
                    {metric.sector}
                  </span>
                  <div className="text-3xl sm:text-4xl font-bold text-primary font-serif">
                    {metric.growth}
                  </div>
                  <div className="text-xs font-semibold text-accent font-default">
                    {metric.label}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground font-default leading-normal">
                  {metric.detail}
                </p>
              </div>)}
          </div>
        </div>

        {/* Direct Collaboration CTA Box */}
        <div className="bg-secondary/40 border border-accent/30 rounded-2xl p-8 sm:p-10 relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-background border border-border text-xs font-medium text-primary">
                <Building2 className="w-3.5 h-3.5" />
                <span>You Work Directly With Me</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-semibold text-primary font-serif">
                My Hands-On Approach to Doncaster Search Dominance
              </h3>
              <p className="text-foreground/90 font-default leading-relaxed text-sm sm:text-base">
                {"I don't assign junior account managers or use generic AI templates. When you partner with me, I personally research your market, clean your technical architecture, and craft high-converting SEO strategies tailored specifically to Doncaster and South Yorkshire."}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {methodologySteps.map((step, i) => <div key={i} data-index={i} className="flex items-start gap-2 text-xs sm:text-sm text-foreground/80">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <span>{step}</span>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 bg-card border border-border rounded-xl p-6 space-y-6 shadow-sm">
              <div className="space-y-2">
                <h4 className="text-lg font-semibold text-primary font-serif">Ready to Outrank Competitors in Doncaster?</h4>
                <p className="text-xs text-muted-foreground font-default">
                  Book a direct 1-on-1 consultation to discuss your keyword opportunities, technical performance, and organic growth potential.
                </p>
              </div>

              <div className="space-y-3 pt-2">
                <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full border-border hover:bg-muted font-medium">
                  <Link to="/CaseStudiesPage">
                    Explore My Verified Case Studies
                  </Link>
                </Button>
              </div>

              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-1">
                <Globe2 className="w-3.5 h-3.5 text-accent" />
                <span>No long-term lock-ins • 100% Honest SEO Expertise</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}